import SimpleSchema from 'simpl-schema';
import { map } from 'lodash';
import SourceSchema from '../Sources/Sources';
import ConnectorSchema, { FILE_TYPES, CONNECTOR_SUBTYPES } from '../Connectors/Connectors';

import MapServerElements, { MAPSERVERELEMENTS_TYPES } from '../MapServerElements/MapServerElements';
import requiredIf from '../../modules/schema-conditional-required';
import { STATUS_TYPES } from './taskConstants';

const Connector = ConnectorSchema.schema.omit();
Connector.extend({ _id: String });

const Source = SourceSchema.schema.omit();
Source.extend({ _id: String });

/**
 * The connection parameters for a task in the incoming tasklist.
 *
 * @typedef {object} ConnectionParametersSchemaIn
 * @property {boolean} temporal - Flag to signal that the connection is temporal.
 * @property {boolean} tolocal - Flag to signal that the connection is not to local.
 * @property {string[]} allowedPGTypes - The types of sources allowed for a PostGreSQL connector.
 * Some of [VECTOR,THEMATIC].
 * @property {string[]} allowedFileTypes - The type file of sources allowed.
 * Some of [SHAPE,KML,MDB,GeoJSON,GeoTIFF,ECW,CSV,ZIP,7ZIP,RAR,SENTINEL,EXCEL].
 * @property {string[]} allowedMapServerElementTypes - The type of map server elements allowed.
 * Some of [XYZTiles,WMS,TMS,GeoJSON,CSV].
 */

/**
 * The connection parameters for a task sent to run.
 *
 * @typedef {object} ConnectionParametersSchemaOut
 * @augments ConnectionParametersSchemaIn
 * @property {Connector} connector - The connector for the selected source.
 * @property {Source} source - The selected source.
 * @property {boolean} temporal - Flag to signal that the connection is temporal.
 * @property {boolean} tolocal - Flag to signal that the connection is not to local.
 * @property {boolean} createMapServerElement - Flag to indicate if a map server element shoulb be created from this source.
 * @property {string} [mapServerElementType] - The type of the map server element to create. One of allowedMapServerElementTypes.
 * Required if createMapServerElement is true.
 * @property {boolean} [autorefresh] - Flag to indicate if the map server element should autorefresh.
 * Required if createMapServerElement is true.
 * @property {number} [refreshLatency] - The refresh latency for the map server element.
 * Required if autorefresh is false.
 */

/**
 * The connection parameters for a task in the monitor.
 *
 * @typedef {object} ConnectionParametersSchema
 * @augments ConnectionParametersSchemaOut
 * @property {MapServerElements[]} mapServerElements - The map server elements resulting from the task.
 */

const MapServerElementTasker = MapServerElements.schema.pick('type', 'name', 'url');

MapServerElementTasker.extend({
  sourceId: {
    type: String,
    label: 'The Id of the source that generated the layer server',
    custom() {
      const currentNode = this.key[6];
      const sourceIdPath = `nodes.${currentNode}.connectionParams.source._id`;
      if (this.field(sourceIdPath) === this.value) {
        return SimpleSchema.ErrorTypes.VALUE_NOT_ALLOWED;
      }
      return null;
    }
  }
});

export const ConnectionParametersSchemaMonitor = new SimpleSchema({
  connector: {
    type: Connector,
    label: 'The connection for the selected source',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: this.siblingField('temporal').value === false,
        schemaInstance: this
      });
    }
  },
  source: {
    type: Source,
    label: 'The selected source',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: this.siblingField('temporal').value === false,
        schemaInstance: this
      });
    }
  },
  mapServerElements: {
    type: Array,
    label: 'The resuling Layer Servers for this connection',
    optional: true,
    custom() {
      const shouldCreateMapServerElement =
        this.siblingField('createMapServerElement').value === true;
      const isTaskDone = this.field('status').value === STATUS_TYPES.DONE;
      return requiredIf({
        requiredCondition: isTaskDone && shouldCreateMapServerElement,
        schemaInstance: this,
        shouldArrayBeEmpty: false
      });
    }
  },
  'mapServerElements.$': {
    type: MapServerElementTasker,
    label: 'A resulting Layer Server for the connection'
  },
  temporal: {
    type: Boolean,
    label: 'Flag to signal that the connection is temporal',
    optional: true
  },
  tolocal: {
    type: Boolean,
    label: 'Flag to signal that the connection is not to local',
    optional: true
  },
  createMapServerElement: {
    type: Boolean,
    label: 'Flag to indicate if a layer server should be created',
    custom() {
      const isTemporal = this.siblingField('temporal').value === true;
      if (isTemporal && this.value) {
        return 'Cannot create Map Sever Element on a Temporal Connection';
      }
      return undefined;
    }
  },
  mapServerElementType: {
    type: String,
    label: 'The type of the layer server to create',
    allowedValues: map(MAPSERVERELEMENTS_TYPES.properties, 'value'),
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: this.siblingField('createMapServerElement').value === true,
        schemaInstance: this
      });
    }
  },
  autorefresh: {
    type: Boolean,
    label: 'Flag to indicate if the layer server should autorefresh',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: this.siblingField('createMapServerElement').value === true,
        schemaInstance: this
      });
    }
  },
  refreshLatency: {
    type: String,
    label: 'The refresh latency for the layer server. In case it does not autorefresh',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: this.siblingField('autorefresh').value === false,
        schemaInstance: this
      });
    }
  },
  allowedPGTypes: {
    type: Array,
    label: 'The types of sources allowed for a PostGreSQL connection'
  },
  'allowedPGTypes.$': {
    type: String,
    label: 'A type of source allowed for a PostGreSQL connection',
    allowedValues: [CONNECTOR_SUBTYPES.VECTOR, CONNECTOR_SUBTYPES.THEMATIC]
  },
  allowedFileTypes: {
    type: Array,
    label: 'The type file of sources allowed'
  },
  'allowedFileTypes.$': {
    type: String,
    label: 'A type file of source allowed',
    allowedValues: map(FILE_TYPES.properties, 'value')
  },
  allowedMapServerElementTypes: {
    type: Array,
    label: 'The type of layer servers allowed'
  },
  'allowedMapServerElementTypes.$': {
    type: String,
    label: 'A type of layer server allowed',
    allowedValues: map(MAPSERVERELEMENTS_TYPES.properties, 'value')
  }
});

export const ConnectionParametersSchemaOut = ConnectionParametersSchemaMonitor.omit(
  'mapServerElements'
);

export const ConnectionParametersSchemaIn = ConnectionParametersSchemaMonitor.pick(
  'temporal',
  'tolocal',
  'allowedPGTypes',
  'allowedFileTypes',
  'allowedMapServerElementTypes'
);
